/**
 * Distribute one header amount across a sub-array, in proportion to a weight.
 *
 * A document discount cannot simply be subtracted from the total before VAT is
 * charged. Given 100 at 25% and 100 at 0%, taking 20 off and charging 25% on
 * what remains charges VAT on part of a zero-rated sale. The discount has to
 * reach each VAT group in proportion to that group's share of the subtotal, and
 * VAT is charged on what is left (36.03 §5).
 *
 * A percentage discount needs no separate path: reducing every group by the same
 * percentage *is* proportional allocation, so both forms share this one.
 *
 * ## The remainder
 *
 * Shares rounded independently do not necessarily sum to the amount. Three
 * groups of 100 sharing 10 give 3.33 three times — 9.99, a cent short, and the
 * document would not balance. So the difference goes to the largest weight
 * (first one wins a tie), which makes the allocation exact by construction and
 * puts the arbitrary cent where it is least visible.
 *
 * A zero or missing total weight allocates nothing: there is nothing to
 * discount, whatever was typed.
 *
 * Config:
 * {
 *     logicType: 'calcFields',
 *     operationType: 'sub.allocate',
 *     subEntity: 'vatTaxes',
 *     sourceField: 'documentDiscountAmount',         // the header amount
 *     weightField: 'vatTaxableAmountBeforeDiscount', // each element's share basis
 *     targetField: 'documentDiscountAmount',         // written per element
 *     decimals: 2
 * }
 */
const subAllocate = (data, operation) => {
    const { subEntity, sourceField, weightField, targetField, decimals = 2 } = operation

    if (!data || !Array.isArray(data[subEntity])) {
        return data
    }

    const factor = 10 ** decimals
    const round = (n) => Math.round(n * factor) / factor

    const rows = data[subEntity]
    const amount = Number(data[sourceField])
    const weights = rows.map(elem => Number(elem[weightField]) || 0)
    const totalWeight = weights.reduce((sum, w) => sum + w, 0)

    if (!Number.isFinite(amount) || amount === 0 || totalWeight === 0) {
        return {
            ...data,
            [subEntity]: rows.map(elem => ({ ...elem, [targetField]: 0 }))
        }
    }

    const shares = weights.map(weight => round(amount * weight / totalWeight))

    // Whatever rounding lost or gained, settled on the largest weight.
    const allocated = shares.reduce((sum, share) => sum + share, 0)
    const remainder = round(amount - allocated)

    if (remainder !== 0) {
        let largest = 0
        for (let i = 1; i < weights.length; i++) {
            if (weights[i] > weights[largest]) largest = i
        }
        shares[largest] = round(shares[largest] + remainder)
    }

    return {
        ...data,
        [subEntity]: rows.map((elem, i) => ({ ...elem, [targetField]: shares[i] }))
    }
}

export default subAllocate
